import React, { useEffect, useState } from "react";
import { Box, Flex, Text, Avatar, AvatarGroup, useDisclosure } from "@chakra-ui/react";
import { Draggable } from "react-beautiful-dnd";
import TaskModal from "./TaskModal";
import { FORUM, TRANSITION } from "../Theme/styles";
import { parseDate, formatShort, daysWord } from "../dates";

function describeDeadline(deadline) {
  const data = parseDate(deadline);
  if (!data) return null;

  const dzis = new Date();
  const doPolnocy = (d) => new Date(d.getFullYear(), d.getMonth(), d.getDate());
  const days = Math.round((doPolnocy(data) - doPolnocy(dzis)) / 86400000);

  const tekst = formatShort(deadline);
  if (days < 0) return { tekst, kolor: "#F08A80", tytul: `Po terminie o ${-days} ${daysWord(days)}` };
  if (days === 0) return { tekst, kolor: "#F08A80", tytul: "Termin dzisiaj" };
  if (days <= 3) return { tekst, kolor: "#E0A45C", tytul: `Zostały ${days} ${daysWord(days)}` };
  return { tekst, kolor: FORUM.tekstPrzygaszony, tytul: `Termin za ${days} ${daysWord(days)}` };
}

function teamsWord(count) {
  if (count === 1) return "zespół";
  if (count % 10 >= 2 && count % 10 <= 4 && (count % 100 < 12 || count % 100 > 14)) return "zespoły";
  return "zespołów";
}

const PRIORITY = {
  wysoki: { tekst: "#F08A80", name: "WYSOKI" },
  sredni: { tekst: "#E0A45C", name: "ŚREDNI" },
  niski: { tekst: FORUM.tekstPrzygaszony, name: "NISKI" },
};

const TaskItem = (props) => {
  const [people, setPeople] = useState({});
  const { isOpen, onOpen, onClose } = useDisclosure();

  useEffect(() => {
    const foundPeople =
      props.peoples.find((people) => people.id === props.assignedTo) || null;
    setPeople(foundPeople);
  }, [props.assignedTo, props.peoples]);

  const assignees = (props.osoby || [])
    .map((id) => props.peoples.find((o) => o.id === id))
    .filter(Boolean);

  const moje =
    (props.osoby || []).includes(localStorage.getItem("id")) && !props.pokazKategorie;
  const deadline = describeDeadline(props.deadlinePrzydzialu || props.deadline);
  const priorytet = PRIORITY[props.piority] || PRIORITY.niski;
  const komplet = props.ilePozycji > 0 && props.ileZrobionych === props.ilePozycji;

  return (
    <>
      <Draggable
        draggableId={props.draggableId.toString()}
        index={props.index}
        isDragDisabled={props.isDragDisabled}
      >
        {(provided) => (
          <Box
            ref={provided.innerRef}
            {...provided.draggableProps}
            {...provided.dragHandleProps}
            onClick={onOpen}
            cursor="pointer"
          >
            <Flex
              direction={"row"}
              align={"stretch"}
              bgColor={moje ? "#252A36" : FORUM.panelJasny}
              borderRadius="md"
              overflow="hidden"
              border={"1px solid"}
              borderColor={moje ? "#6C99FF" : "transparent"}
              transition={TRANSITION}
              _hover={{
                transform: "scale(1.02)",
                bgColor: moje ? "#2A3040" : "#2A2C34",
                "& .tytul-zadania": { color: FORUM.tekstJasny },
              }}
            >
              <Flex
                flex="1"
                minW={0}
                direction={"row"}
                justifyContent="space-between"
                alignItems={"center"}
                gap={3}
                px={3}
                py={2}
              >
                <Flex direction={"column"} minW={0} gap={0.5}>
                <Text
                  className="tytul-zadania"
                  fontSize="sm"
                  fontWeight="medium"
                  color={FORUM.tekst}
                  transition={TRANSITION}
                  noOfLines={2}
                  title={props.title}
                >
                  {props.title}
                </Text>
                {props.zajetePrzez && (
                  <Text fontSize={"10px"} color={"#4FB477"} noOfLines={1}>
                    wziął: {props.people?.find((o) => o.id === props.zajetePrzez)?.nickname || "ktoś"}
                  </Text>
                )}
                {(props.ilePozycji > 0 || props.inneGrupy > 0) && (
                  <Text fontSize={"10px"} color={FORUM.tekstPrzygaszony} noOfLines={1}>
                    {props.ilePozycji > 0 && (
                      <Text
                        as={"span"}
                        color={komplet ? "#4FB477" : FORUM.tekstPrzygaszony}
                        fontWeight={komplet ? "600" : "400"}
                      >
                        {props.ileZrobionych}/{props.ilePozycji}
                      </Text>
                    )}
                    {props.ilePozycji > 0 && props.inneGrupy > 0 && " · "}
                    {props.inneGrupy > 0 && `+${props.inneGrupy} ${teamsWord(props.inneGrupy)}`}
                  </Text>
                )}
                {props.categoryName && (
                  <Text
                    fontSize={"10px"}
                    color={FORUM.tekstPrzygaszony}
                    noOfLines={1}
                  >
                    {props.categoryName}
                  </Text>
                )}
                </Flex>

                <Flex align="center" gap={2} flexShrink={0}>
                  <Text
                    fontSize="10px"
                    fontWeight="semibold"
                    letterSpacing="wide"
                    color={priorytet.tekst}
                    bg={FORUM.plakietka}
                    borderRadius="full"
                    px={2}
                    py="1px"
                    title={props.piority}
                  >
                    {priorytet.name}
                  </Text>

                  {deadline && (
                    <Text
                      fontSize="10px"
                      fontWeight="semibold"
                      color={deadline.kolor}
                      bg={FORUM.plakietka}
                      borderRadius="full"
                      px={2}
                      py="1px"
                      title={deadline.tytul}
                      whiteSpace={"nowrap"}
                    >
                      {deadline.tekst}
                    </Text>
                  )}

                  {moje && (
                    <Text
                      fontSize="10px"
                      fontWeight="bold"
                      color={"#6C99FF"}
                      bg={"rgba(108,153,255,0.12)"}
                      borderRadius="full"
                      px={2}
                      py="1px"
                      title={"Przypisane do Ciebie"}
                    >
                      TY
                    </Text>
                  )}

                {assignees.length > 1 ? (
                  <AvatarGroup size="xs" max={3} flexShrink={0}>
                    {assignees.map((o) => (
                      <Avatar key={o.id} name={o.nickname} src={o.avatar} title={o.nickname} />
                    ))}
                  </AvatarGroup>
                ) : (
                  people && (
                    <Avatar
                      size="xs"
                      name={people?.nickname}
                      src={people?.avatar}
                      title={people?.nickname}
                      flexShrink={0}
                    />
                  )
                )}
                </Flex>
              </Flex>
            </Flex>
          </Box>
        )}
      </Draggable>

      <TaskModal
        isOpen={isOpen}
        onClose={onClose}
        taskId={props.taskId || props.id}
        assignmentId={props.id}
        peoples={props.peoples}
        category={props.category}
      />
    </>
  );
};

export default TaskItem;
